/**
 * What 3 Ingredients - Smart Ingredient Parser
 * Takes raw user text input (even unformatted or messy) and extracts:
 * - Up to 3 MAIN ingredients
 * - Separate UTILITY ingredients (oil, salt, pepper, water, etc. which don't count towards the limit)
 */

import { isUtilityStaple } from '../data/utilityStaples.js';

// Common stop words to ignore during parsing
const STOP_WORDS = new Set([
  'and', 'with', 'a', 'an', 'the', 'some', 'few', 'of', 'in', 'on', 'or', 'for',
  'fresh', 'raw', 'sliced', 'diced', 'chopped', 'minced', 'cup', 'cups', 'tbsp',
  'tsp', 'oz', 'gram', 'grams', 'lb', 'lbs', 'piece', 'pieces', 'can', 'cans',
  'bottle', 'bottles', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0'
]);

/**
 * Parses raw freeform text input into main and utility ingredients
 * @param {string} rawInput 
 * @returns {{ mainIngredients: string[], utilityIngredients: string[], rawInput: string }}
 */
export function parseIngredients(rawInput) {
  if (!rawInput || typeof rawInput !== 'string') {
    return { mainIngredients: [], utilityIngredients: [], rawInput: '' };
  }

  // Split by commas, plus signs, newlines, or ' and '
  const rawTokens = rawInput
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\+/g, ',')
    .split(/[,;\n]/)
    .flatMap(item => item.split(/\s+and\s+/));

  const mainList = [];
  const utilityList = [];

  rawTokens.forEach(token => {
    // Clean token
    let cleaned = token.replace(/[^a-z0-9\s]/g, '').trim();
    if (!cleaned) return;

    // Filter out leading numbers/units
    const words = cleaned.split(/\s+/).filter(w => !STOP_WORDS.has(w));
    if (words.length === 0) return;

    const candidate = words.join(' ');

    if (isUtilityStaple(candidate)) {
      if (!utilityList.includes(candidate)) {
        utilityList.push(candidate);
      }
    } else {
      if (!mainList.includes(candidate)) {
        mainList.push(candidate);
      }
    }
  });

  // Strict cap: Keep no more than 3 main ingredients!
  const finalMain = mainList.slice(0, 3);

  return {
    mainIngredients: finalMain,
    utilityIngredients: utilityList,
    rawInput: rawInput.trim()
  };
}
